/* global axios */
import ApiClient from './ApiClient';

class InternalChatAPI extends ApiClient {
  constructor() {
    super('internal_chat/channels', { accountScoped: true });
  }

  getChannels() {
    return axios.get(this.url);
  }

  getChannel(channelId) {
    return axios.get(`${this.url}/${channelId}`);
  }

  updateChannel(channelId, attributes) {
    return axios.patch(`${this.url}/${channelId}`, { channel: attributes });
  }

  deleteChannel(channelId) {
    return axios.delete(`${this.url}/${channelId}`);
  }

  archiveChannel(channelId) {
    return axios.post(`${this.url}/${channelId}/archive`);
  }

  unarchiveChannel(channelId) {
    return axios.post(`${this.url}/${channelId}/unarchive`);
  }

  markUnread(channelId) {
    return axios.post(`${this.url}/${channelId}/mark_unread`);
  }

  getCategories() {
    return axios.get(`${this.baseUrl()}/internal_chat/categories`);
  }

  createCategory(attributes) {
    return axios.post(`${this.baseUrl()}/internal_chat/categories`, {
      category: attributes,
    });
  }

  updateCategory(categoryId, attributes) {
    return axios.patch(
      `${this.baseUrl()}/internal_chat/categories/${categoryId}`,
      {
        category: attributes,
      }
    );
  }

  deleteCategory(categoryId) {
    return axios.delete(
      `${this.baseUrl()}/internal_chat/categories/${categoryId}`
    );
  }

  getMembers(channelId) {
    return axios.get(`${this.url}/${channelId}/members`);
  }

  addMember(channelId, attributes) {
    return axios.post(`${this.url}/${channelId}/members`, {
      member: attributes,
    });
  }

  removeMember(channelId, membershipId, type = 'user') {
    const params = type === 'team' ? '?member_type=team' : '';
    return axios.delete(
      `${this.url}/${channelId}/members/${membershipId}${params}`
    );
  }

  getMessages(channelId, params = {}) {
    return axios.get(`${this.url}/${channelId}/messages`, { params });
  }

  getThread(channelId, messageId, params = {}) {
    return axios.get(`${this.url}/${channelId}/messages/${messageId}/thread`, {
      params,
    });
  }

  getMessage(channelId, messageId) {
    return axios.get(`${this.url}/${channelId}/messages/${messageId}`);
  }

  createChannel({
    channelType,
    name,
    description,
    userIds = [],
    teamIds = [],
  }) {
    return axios.post(this.url, {
      channel: {
        channel_type: channelType,
        name,
        description,
        user_ids: userIds,
        team_ids: teamIds,
      },
    });
  }

  createMessage(
    channelId,
    {
      content,
      contentFormat,
      attachments = [],
      echoId,
      parentId,
      alsoSendInChannel = false,
      mentionedUserIds = [],
      mentionedTeamIds = [],
      mentionAll = false,
    }
  ) {
    const form = new FormData();
    form.append('message[content]', content);
    if (contentFormat) form.append('message[content_format]', contentFormat);
    form.append('message[echo_id]', echoId);
    if (parentId) form.append('message[parent_id]', parentId);
    if (parentId && alsoSendInChannel)
      form.append('message[also_send_in_channel]', '1');
    mentionedUserIds.forEach(id =>
      form.append('message[mentioned_user_ids][]', id)
    );
    mentionedTeamIds.forEach(id =>
      form.append('message[mentioned_team_ids][]', id)
    );
    if (mentionAll) form.append('message[mention_all]', '1');
    attachments.forEach(file => form.append('message[attachments][]', file));
    return axios.post(`${this.url}/${channelId}/messages`, form);
  }

  updateMessage(channelId, messageId, attributes) {
    return axios.patch(`${this.url}/${channelId}/messages/${messageId}`, {
      message: attributes,
    });
  }

  deleteMessage(channelId, messageId) {
    return axios.delete(`${this.url}/${channelId}/messages/${messageId}`);
  }

  markRead(channelId) {
    return axios.post(`${this.url}/${channelId}/mark_read`);
  }

  getNotifications() {
    return axios.get(`${this.baseUrl()}/internal_chat/notifications`);
  }

  readNotification(notificationId) {
    return axios.post(
      `${this.baseUrl()}/internal_chat/notifications/${notificationId}/read`
    );
  }

  readAllNotifications() {
    return axios.post(`${this.baseUrl()}/internal_chat/notifications/read_all`);
  }

  createReaction(channelId, messageId, emoji) {
    return axios.post(
      `${this.url}/${channelId}/messages/${messageId}/reactions`,
      { reaction: { emoji } }
    );
  }

  deleteReaction(channelId, messageId, reactionId) {
    return axios.delete(
      `${this.url}/${channelId}/messages/${messageId}/reactions/${reactionId}`
    );
  }

  getDraft(channelId, parentId = null) {
    const params = parentId ? { parent_id: parentId } : {};
    return axios.get(`${this.url}/${channelId}/draft`, { params });
  }

  saveDraft(channelId, { parentId = null, content, contentAttributes = {} }) {
    return axios.patch(`${this.url}/${channelId}/draft`, {
      draft: {
        parent_id: parentId,
        content,
        content_attributes: contentAttributes,
      },
    });
  }

  deleteDraft(channelId, parentId = null) {
    const params = parentId ? { parent_id: parentId } : {};
    return axios.delete(`${this.url}/${channelId}/draft`, { params });
  }

  setTyping(channelId, state) {
    return axios.post(`${this.url}/${channelId}/typing`, { typing: { state } });
  }

  getDrafts() {
    return axios.get(`${this.baseUrl()}/internal_chat/drafts`);
  }

  search(query, params = {}) {
    return axios.get(`${this.baseUrl()}/internal_chat/search`, {
      params: { q: query, ...params },
    });
  }
}

export default new InternalChatAPI();
